import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ConnectionSearchItem, UseQueryWrapper } from 'app/types';
import { useSnakeBar } from 'app/hooks/useSnakeBar';
import { searchConnections } from 'app/Domain/Connections/Services/ConnectionService';

export type SearchConnectionsQueryResultData = {
  page: number;
  pageSize: number;
  totalItems: number;
  items: ConnectionSearchItem[];
};

export type SearchConnectionsQueryVariables = {
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  ownerId?: string;
};

const queryFn =
  ({ page, pageSize, searchQuery, sortBy, sortOrder }: SearchConnectionsQueryVariables) =>
  async (): Promise<SearchConnectionsQueryResultData> => {
    const response = await searchConnections({ page, pageSize, searchQuery, sortBy, sortOrder });
    if ('cancelPrevQuery' in response) {
      return { page: 0, pageSize: 0, totalItems: 0, items: [] };
    }
    return response.data as SearchConnectionsQueryResultData;
  };

const QUERY_KEY = 'searchConnections';

export const useConnectionsSearchQuery: UseQueryWrapper<
  SearchConnectionsQueryResultData,
  SearchConnectionsQueryVariables
> = ({ variables, options = {} }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  return useQuery<SearchConnectionsQueryResultData>(
    [
      QUERY_KEY,
      variables.ownerId,
      variables.page,
      variables.pageSize,
      variables.searchQuery,
      variables.sortBy,
      variables.sortOrder,
    ],
    queryFn(variables),
    {
      onError,
      keepPreviousData: true,
      staleTime: 3 * 60 * 1000,
      ...options,
    },
  );
};
